import useTranslation from 'next-translate/useTranslation'
import { FlexboxGrid } from 'rsuite'

import { getPalletSize, getPalletTotalUnits, getPalletUnitsText } from '../../lib/product'
import { getPerBoxItems, getPerCartonItems, getSalesUnit, isPairProduct } from '../../lib/salesUnit'
import { convertToOneDecimal, formatGenericIntegerNumber } from '../../utils/math'
import { Product } from '../../utils/types/Product'
import BoxIcon from '../Icons/SalesUnitSizes/BoxIcon'
import PackageIcon from '../Icons/SalesUnitSizes/PackageIcon'
import PalletIcon from '../Icons/SalesUnitSizes/PalletIcon'
import PieceIcon from '../Icons/SalesUnitSizes/PieceIcon'

import styles from '../../styles/SalesUnitSizes.module.less'

interface SalesUnitSizesProps {
  className?: string
  product: Product
}

const SalesUnitSizes = ({ className, product }: SalesUnitSizesProps) => {
  const { t } = useTranslation('products')
  const perPackage = getPerBoxItems(product)
  const perCarton = getPerCartonItems(product)
  const isPair = isPairProduct(product)
  const salesUnit = getSalesUnit(product)
  const totalPalletUnits = getPalletTotalUnits(product)

  const unitTypeTranslationOne = isPair ? t('pair_one') : t('piece_one')
  const unitTypeTranslationOther = isPair ? t('pair_other') : t('piece_other')

  // TODO: Create HOC / Generalized component to reduce repetition
  const Piece = ({ highlight }: { highlight?: boolean }) => (
    <FlexboxGrid.Item className={`single-sales-unit-container sales-unit-piece ${highlight && 'highlight'}`}>
      <PieceIcon />
      <p className="single-sales-unit-name">{t('In units of one piece')}</p>
      <p className="single-sales-unit-size">{`1 ${unitTypeTranslationOne}`}</p>
    </FlexboxGrid.Item>
  )

  const Pair = ({ highlight }: { highlight?: boolean }) => (
    <FlexboxGrid.Item className={`single-sales-unit-container sales-unit-piece ${highlight && 'highlight'}`}>
      <PieceIcon />
      <p className="single-sales-unit-name">{t('Sold in units of one pair')}</p>
      <p className="single-sales-unit-size">{`1 ${unitTypeTranslationOne}`}</p>
    </FlexboxGrid.Item>
  )

  const Package = ({ highlight }: { highlight?: boolean }) => (
    <FlexboxGrid.Item className={`single-sales-unit-container ${highlight && 'highlight'}`}>
      <PackageIcon />
      <p className="single-sales-unit-name">{t('In a package')}</p>
      <p className="single-sales-unit-size">{perPackage > 1 ? `${perPackage} ${unitTypeTranslationOther}` : `${perPackage} ${unitTypeTranslationOne}`}</p>
    </FlexboxGrid.Item>
  )

  const Carton = ({ highlight }: { highlight?: boolean }) => (
    <FlexboxGrid.Item className={`single-sales-unit-container ${highlight && 'highlight'}`}>
      <BoxIcon />
      <p className="single-sales-unit-name">{t('In a carton')}</p>
      <p className="single-sales-unit-size">{perCarton > 1 ? `${perCarton} ${t('package_other')}` : `${perCarton} ${t('package_one')}`}</p>
      <p className="single-sales-unit-piece-amount">{`${formatGenericIntegerNumber(perCarton * perPackage)} ${(perCarton * perPackage) > 1 ? unitTypeTranslationOther : unitTypeTranslationOne}`}</p>
    </FlexboxGrid.Item>
  )

  const Pallet = ({ highlight }: { highlight?: boolean }) => {
    const renderHigherSaleUnitQuantity = () => {
      const palletSize = getPalletSize(product)
      const palletUnitsText = `${palletSize} ${getPalletUnitsText(product, t)}`

      if (salesUnit !== 'carton' && !!perCarton) {
        const cartonPalletQuantity = convertToOneDecimal(
          palletSize / ((perPackage || 1) * perCarton),
        )
        return (
          <>
            <p className="single-sales-unit-size">{`${cartonPalletQuantity} cartons`}</p>
            <p className="single-sales-unit-piece-amount">
              {palletUnitsText}
            </p>
          </>
        )
      }

      return (
        <p className="single-sales-unit-size">{palletUnitsText}</p>
      )
    }

    return (
      <FlexboxGrid.Item className={`single-sales-unit-container ${highlight && 'highlight'}`}>
        <PalletIcon />
        <p className="single-sales-unit-name">{t('On a pallet')}</p>
        {renderHigherSaleUnitQuantity()}
        {salesUnit !== 'piece' && (
        <p className="single-sales-unit-piece-amount">
          {`${totalPalletUnits} ${Number(totalPalletUnits) > 1 ? unitTypeTranslationOther : unitTypeTranslationOne}`}
        </p>
        )}
      </FlexboxGrid.Item>
    )
  }

  const renderSalesUnits = () => {
    if (perCarton) {
      return (
        <>
          <Package />
          <Carton highlight />
        </>
      )
    }
    if (perPackage) {
      return <Package highlight />
    }
    if (isPair) {
      return <Pair highlight />
    }

    return <Piece highlight />
  }

  return (
    <div
      data-testid="sales-unit-sizes-main-wrapper"
      className={`${styles['sales-unit-sizes-main-wrapper']} ${className}`}
    >
      <div className="h5 sales-unit-sizes-title">
        {t('Packaging details')}
      </div>
      <FlexboxGrid className="sales-unit-sizes-wrapper">
        {renderSalesUnits()}
        {!!totalPalletUnits && <Pallet />}
      </FlexboxGrid>
    </div>
  )
}

export default SalesUnitSizes
